import { useEffect, useState, useCallback } from 'react';
import { getNotifications, markNotificationRead, markAllRead } from '../../lib/api';
import { Bell, CheckCircle, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import MobileLoanDetail from './MobileLoanDetail';

function TimeAgo({ iso }) {
  if (!iso) return null;
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return <span className="text-[10px] text-muted-foreground">now</span>;
  if (diff < 3600) return <span className="text-[10px] text-muted-foreground">{Math.floor(diff / 60)}m</span>;
  if (diff < 86400) return <span className="text-[10px] text-muted-foreground">{Math.floor(diff / 3600)}h</span>;
  return <span className="text-[10px] text-muted-foreground">{Math.floor(diff / 86400)}d</span>;
}

export default function MobileUpdates({ user }) {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('unread');
  const [openFile, setOpenFile] = useState(null);

  const load = useCallback(async () => {
    try { setNotifs(await getNotifications()); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load]);

  const unread = notifs.filter(n => !n.read);
  const read = notifs.filter(n => n.read);
  const list = tab === 'unread' ? unread : read;

  const handleTap = async (n) => {
    try { await markNotificationRead(n.id); } catch (e) {}
    if (n.file_id) setOpenFile(n.file_id);
    load();
  };
  const handleAckAll = async () => {
    try { await markAllRead(); toast.success('All marked read'); load(); } catch (e) { toast.error('Could not mark all read'); }
  };

  return (
    <div className="pb-4" data-testid="mobile-updates">
      <div className="flex items-center justify-between px-1 pt-2 pb-2">
        <div>
          <h1 className="text-lg font-bold text-foreground leading-tight">Updates</h1>
          <p className="text-[11px] text-muted-foreground">{unread.length} unread · {read.length} read</p>
        </div>
        {unread.length > 0 && (
          <button onClick={handleAckAll} data-testid="updates-ack-all" className="text-[11px] font-semibold text-[color:var(--mr-blue)]">Mark all read</button>
        )}
      </div>
      <div className="flex gap-0.5 bg-muted rounded-lg p-0.5 mb-3">
        <button onClick={() => setTab('unread')} data-testid="updates-tab-unread"
          className={`flex-1 py-2 text-xs font-bold rounded-md ${tab === 'unread' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>
          Unread {unread.length ? <span className="ml-1 text-[10px] px-1.5 rounded chip-critical border">{unread.length}</span> : null}
        </button>
        <button onClick={() => setTab('read')} data-testid="updates-tab-read"
          className={`flex-1 py-2 text-xs font-bold rounded-md ${tab === 'read' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>
          Read
        </button>
      </div>

      {loading && !notifs.length ? (
        <div className="space-y-2">{[0,1,2].map(i => <div key={i} className="h-14 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : list.length === 0 ? (
        <div className="text-center py-10" data-testid="updates-empty">
          <CheckCircle size={28} weight="duotone" className="text-[color:var(--mr-ok)] mx-auto mb-2" />
          <p className="text-sm font-bold text-foreground">{tab === 'unread' ? 'All caught up' : 'Nothing here yet'}</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {list.map(n => (
            <button key={n.id} onClick={() => handleTap(n)} data-testid={`update-${n.id}`}
              className={`w-full text-left mr-card p-2.5 flex items-start gap-2 min-h-[56px] active:bg-muted/40 ${!n.read ? 'bg-[color:var(--mr-blue-wash)]/30' : ''}`}>
              <span className={`w-8 h-8 rounded-md flex items-center justify-center text-[10px] font-black flex-shrink-0 ${n.severity === 'critical' ? 'chip-critical' : 'chip-info'} border`}>{(n.from_member || '•').slice(0,2).toUpperCase()}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-bold text-foreground truncate">{n.from_member || 'Team'}</span>
                  {n.borrower && <span className="text-[10px] text-muted-foreground truncate">· {n.borrower}</span>}
                  <TimeAgo iso={n.created_at} />
                </div>
                <p className="text-xs text-foreground leading-snug">{n.message}</p>
                {n.action_required && !n.read && <span className="inline-block mt-1 text-[9px] font-bold uppercase tracking-wider chip-warn border px-1.5 py-0.5 rounded">Action needed</span>}
              </div>
              {!n.read && <span className="w-2 h-2 rounded-full bg-[color:var(--mr-blue)] flex-shrink-0 mt-2" />}
              <CaretRight size={14} className="text-muted-foreground flex-shrink-0 mt-2" />
            </button>
          ))}
        </div>
      )}

      {openFile && <MobileLoanDetail fileId={openFile} onClose={() => setOpenFile(null)} currentUser={user} />}
    </div>
  );
}
